import { useCallback, useState } from 'react';
import { Button, LoadingSpinner } from '@/components/ui';
import { metaAdsApi, type MetaAdAccountSummary } from '@/services/api/metaAds';
import { debug } from '@/utils/debug';
import { formatAccountId as formatMetaAccountId } from '@/utils/metaAds';
import { useInvalidateLinkageEligibility } from '@/hooks/queries/useLinkageEligibilityQuery';

interface MetaAccountPickerProps {
  accounts: MetaAdAccountSummary[];
  state: string;
  onLinked: (account: MetaAdAccountSummary) => void;
  onError: (err: unknown) => void;
}

/**
 * Account selection screen after the Meta OAuth round-trip lands at
 * /oauth/callback/meta. Parallel of Google's `GoogleAdsLinker/index.tsx`
 * (the customer-ID input modal): once the user makes a choice, the parent
 * state machine moves to `linking → success`.
 *
 * In-flight UX (mirrors Google's submit-disabled-while-connecting pattern):
 * the clicked card shows an inline spinner + "Linking…" label; every other
 * card's Connect button is disabled until the call resolves.
 */
export function MetaAccountPicker({
  accounts,
  state,
  onLinked,
  onError,
}: MetaAccountPickerProps) {
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const invalidateEligibility = useInvalidateLinkageEligibility();

  const handleConnect = useCallback(
    async (account: MetaAdAccountSummary) => {
      if (submittingId !== null) return;
      debug.log('MetaAccountPicker: Connecting account', {
        meta_ad_account_id: account.meta_ad_account_id,
      });
      setSubmittingId(account.meta_ad_account_id);
      try {
        await metaAdsApi.linkAccount({
          meta_ad_account_id: account.meta_ad_account_id,
          state,
          account_name: account.account_name,
          currency: account.currency,
          timezone_name: account.timezone_name,
          business_id: account.business_id,
          business_name: account.business_name,
        });
        invalidateEligibility();
        onLinked(account);
      } catch (err) {
        debug.error('MetaAccountPicker: Link failed', err);
        setSubmittingId(null);
        onError(err);
      }
    },
    [invalidateEligibility, onError, onLinked, state, submittingId],
  );

  const modalTitleId = 'meta-account-picker-title';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={modalTitleId}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3
            id={modalTitleId}
            className="text-lg font-semibold text-gray-900 m-0"
          >
            Choose a Meta Ad Account
          </h3>
          <p className="text-sm text-gray-600 mt-1 mb-0">
            Pick the ad account you want to connect to Flouri.
          </p>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {accounts.length === 0 ? (
            <p className="text-sm text-gray-600">
              No Meta ad accounts were returned for your Meta user. Confirm
              you authorized the right Business and try again.
            </p>
          ) : (
            <ul className="flex flex-col gap-3" role="list">
              {accounts.map((account) => {
                const isSubmitting =
                  submittingId === account.meta_ad_account_id;
                const otherSubmitting =
                  submittingId !== null && !isSubmitting;
                // Centralized via `utils/metaAds.ts:formatAccountId` — strip
                // the `act_` namespace prefix everywhere we surface the ID to
                // the user (Home dashboard does the same; see comment at
                // `pages/Home/index.tsx`). The backend keeps the prefix.
                const displayId = formatMetaAccountId(account.meta_ad_account_id);
                const name = account.account_name || displayId;
                const subtitleParts: string[] = [displayId];
                if (account.currency) subtitleParts.push(account.currency);
                if (account.timezone_name)
                  subtitleParts.push(account.timezone_name);
                return (
                  <li
                    key={account.meta_ad_account_id}
                    className="border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between gap-4"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-900 truncate">
                        {name}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {subtitleParts.join(' · ')}
                      </div>
                      {account.business_id && (
                        <div className="mt-1 inline-block text-[10px] uppercase tracking-wide text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">
                          Business {account.business_id}
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      {isSubmitting ? (
                        <div
                          className="flex items-center gap-2 text-sm text-gray-600"
                          aria-live="polite"
                        >
                          <LoadingSpinner size="sm" variant="primary" />
                          <span>Linking…</span>
                        </div>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleConnect(account)}
                          disabled={otherSubmitting}
                          aria-label={`Connect ${name}`}
                        >
                          Connect
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
